import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';

export default function WindowsDoorsPage() {
  const productTypes = [
    {
      name: 'Replacement Windows',
      description: 'Energy-efficient windows that reduce heating and cooling costs while enhancing curb appeal.',
      features: ['Double & triple-pane options', 'Low-E glass coating', 'Vinyl, wood, or fiberglass frames']
    },
    {
      name: 'Entry Doors',
      description: 'Secure, stylish entry doors that make a lasting first impression and improve home security.',
      features: ['Steel, fiberglass, or wood', 'Multi-point locking systems', 'Custom design options']
    },
    {
      name: 'Patio & Sliding Doors',
      description: 'Seamless indoor-outdoor transitions with smooth operation and maximum natural light.',
      features: ['French doors & sliders', 'Energy-efficient glass', 'Durable weather sealing']
    }
  ];

  const benefits = [
    {
      icon: 'ri-flashlight-line',
      title: 'Energy Efficiency',
      description: 'Modern windows and doors can reduce energy bills by up to 25% with superior insulation.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Enhanced Security',
      description: 'Advanced locking systems and reinforced frames provide peace of mind and protection.'
    },
    {
      icon: 'ri-volume-mute-line',
      title: 'Noise Reduction',
      description: 'Multi-pane glass and quality seals significantly reduce outside noise for a quieter home.'
    },
    {
      icon: 'ri-home-heart-line',
      title: 'Increased Home Value',
      description: 'New windows and doors offer excellent ROI and boost your home\'s market appeal.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Windows &amp; Doors Installation
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Upgrade your home with energy-efficient windows and beautiful, secure doors. Our expert installation ensures perfect fit, superior performance, and lasting beauty that enhances your home&apos;s comfort and value.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  window.REACT_APP_NAVIGATE('/');
                  setTimeout(() => {
                    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }}
                className="px-8 py-4 bg-brand text-white rounded-lg font-semibold hover:bg-brand-dark transition-colors whitespace-nowrap cursor-pointer"
              >
                Request Free Estimate
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Product Types Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Product Range</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We offer a comprehensive selection of high-quality windows and doors to suit any style and budget.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {productTypes.map((product, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-8 border border-gray-200 hover:shadow-lg transition-shadow">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{product.name}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{product.description}</p>
                <ul className="space-y-3">
                  {product.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <i className="ri-check-line text-brand text-xl mr-2 flex-shrink-0"></i>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-br from-brand to-brand-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Benefits of New Windows &amp; Doors</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Investing in quality windows and doors provides immediate and long-term advantages.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-xl p-8 text-center hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-gradient-to-br from-brand to-brand-dark rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${benefit.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Materials Guide Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-sm font-semibold text-brand uppercase tracking-widest mb-3">Material Guide</span>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Choosing the Right Material</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Every material has its strengths. We help you pick the one that fits your home, climate, and budget — and we install it to last.
            </p>
          </div>

          {/* Window Frame Materials */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center bg-brand/10 rounded-lg">
                <i className="ri-window-line text-brand text-lg"></i>
              </span>
              Window Frame Materials
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              {/* Vinyl */}
              <div className="rounded-xl border border-gray-200 overflow-hidden hover:border-brand/40 transition-colors">
                <div className="bg-gradient-to-br from-gray-50 to-white p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-xl font-bold text-gray-900">Vinyl (uPVC)</h4>
                    <span className="text-xs font-semibold bg-brand/10 text-brand px-3 py-1 rounded-full">Most Popular</span>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed mb-5">
                    The go-to choice for homeowners who want excellent performance without the maintenance. Vinyl frames are hollow-chambered, which traps air and creates a natural thermal barrier — keeping your home warmer in winter and cooler in summer.
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2 text-gray-700"><i className="ri-check-line text-brand"></i> Zero painting or staining required</li>
                    <li className="flex items-center gap-2 text-gray-700"><i className="ri-check-line text-brand"></i> Resistant to moisture, rot, and insects</li>
                    <li className="flex items-center gap-2 text-gray-700"><i className="ri-check-line text-brand"></i> Excellent U-factor ratings (0.17–0.30)</li>
                    <li className="flex items-center gap-2 text-gray-700"><i className="ri-check-line text-brand"></i> Cost-effective with 20–40 year lifespan</li>
                  </ul>
                </div>
                <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-xs text-gray-500">Best for</span>
                  <span className="text-xs font-semibold text-gray-700">Budget-conscious upgrades</span>
                </div>
              </div>

              {/* Fiberglass */}
              <div className="rounded-xl border border-gray-200 overflow-hidden hover:border-brand/40 transition-colors">
                <div className="bg-gradient-to-br from-gray-50 to-white p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-xl font-bold text-gray-900">Fiberglass</h4>
                    <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-3 py-1 rounded-full">Premium</span>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed mb-5">
                    Fiberglass is the high-performance choice. It expands and contracts at nearly the same rate as glass, which means tighter seals over time and fewer drafts. It can be painted any color and mimics the look of real wood without the upkeep.
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2 text-gray-700"><i className="ri-check-line text-brand"></i> 8× stronger than vinyl under impact</li>
                    <li className="flex items-center gap-2 text-gray-700"><i className="ri-check-line text-brand"></i> Paintable — any color, any time</li>
                    <li className="flex items-center gap-2 text-gray-700"><i className="ri-check-line text-brand"></i> Minimal thermal expansion (tight seals)</li>
                    <li className="flex items-center gap-2 text-gray-700"><i className="ri-check-line text-brand"></i> 50+ year lifespan with minimal care</li>
                  </ul>
                </div>
                <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-xs text-gray-500">Best for</span>
                  <span className="text-xs font-semibold text-gray-700">Long-term investment</span>
                </div>
              </div>

              {/* Wood */}
              <div className="rounded-xl border border-gray-200 overflow-hidden hover:border-brand/40 transition-colors">
                <div className="bg-gradient-to-br from-gray-50 to-white p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-xl font-bold text-gray-900">Wood</h4>
                    <span className="text-xs font-semibold bg-stone-100 text-stone-700 px-3 py-1 rounded-full">Classic</span>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed mb-5">
                    Nothing matches the warmth and character of real wood. It&apos;s a natural insulator, highly customizable, and adds undeniable charm to historic or craftsman-style homes. With proper care, wood windows are a timeless investment.
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2 text-gray-700"><i className="ri-check-line text-brand"></i> Natural insulating properties</li>
                    <li className="flex items-center gap-2 text-gray-700"><i className="ri-check-line text-brand"></i> Fully customizable — stain or paint</li>
                    <li className="flex items-center gap-2 text-gray-700"><i className="ri-check-line text-brand"></i> Ideal for historic homes &amp; restorations</li>
                    <li className="flex items-center gap-2 text-gray-700"><i className="ri-check-line text-brand"></i> Highest aesthetic appeal &amp; home value</li>
                  </ul>
                </div>
                <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-xs text-gray-500">Best for</span>
                  <span className="text-xs font-semibold text-gray-700">Character &amp; curb appeal</span>
                </div>
              </div>
            </div>
          </div>

          {/* Door Materials */}
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-8 flex items-center gap-3">
              <span className="w-8 h-8 flex items-center justify-center bg-brand/10 rounded-lg">
                <i className="ri-door-open-line text-brand text-lg"></i>
              </span>
              Door Materials
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              {/* Steel */}
              <div className="rounded-xl border border-gray-200 overflow-hidden hover:border-brand/40 transition-colors">
                <div className="bg-gradient-to-br from-gray-50 to-white p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-xl font-bold text-gray-900">Steel</h4>
                    <span className="text-xs font-semibold bg-brand/10 text-brand px-3 py-1 rounded-full">Most Secure</span>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed mb-5">
                    Steel doors are the gold standard for security and durability. Filled with polyurethane foam insulation, they outperform wood in energy efficiency and resist warping, cracking, and swelling regardless of weather conditions.
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2 text-gray-700"><i className="ri-check-line text-brand"></i> Highest forced-entry resistance</li>
                    <li className="flex items-center gap-2 text-gray-700"><i className="ri-check-line text-brand"></i> Foam-core insulation (R-value up to 6)</li>
                    <li className="flex items-center gap-2 text-gray-700"><i className="ri-check-line text-brand"></i> Won&apos;t warp, crack, or swell</li>
                    <li className="flex items-center gap-2 text-gray-700"><i className="ri-check-line text-brand"></i> Lower cost than fiberglass</li>
                  </ul>
                </div>
                <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-xs text-gray-500">Best for</span>
                  <span className="text-xs font-semibold text-gray-700">Security &amp; value</span>
                </div>
              </div>

              {/* Fiberglass Door */}
              <div className="rounded-xl border border-gray-200 overflow-hidden hover:border-brand/40 transition-colors">
                <div className="bg-gradient-to-br from-gray-50 to-white p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-xl font-bold text-gray-900">Fiberglass</h4>
                    <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-3 py-1 rounded-full">Best Overall</span>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed mb-5">
                    Fiberglass entry doors combine the beauty of wood with the durability of modern engineering. They resist dents, rust, and rot, and can be finished to look exactly like real wood grain — without any of the maintenance headaches.
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2 text-gray-700"><i className="ri-check-line text-brand"></i> Realistic wood-grain texture options</li>
                    <li className="flex items-center gap-2 text-gray-700"><i className="ri-check-line text-brand"></i> Dent, rust, and rot resistant</li>
                    <li className="flex items-center gap-2 text-gray-700"><i className="ri-check-line text-brand"></i> Superior energy efficiency (R-value 5–6)</li>
                    <li className="flex items-center gap-2 text-gray-700"><i className="ri-check-line text-brand"></i> Minimal maintenance, decades of life</li>
                  </ul>
                </div>
                <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-xs text-gray-500">Best for</span>
                  <span className="text-xs font-semibold text-gray-700">Aesthetics &amp; performance</span>
                </div>
              </div>

              {/* Wood Door */}
              <div className="rounded-xl border border-gray-200 overflow-hidden hover:border-brand/40 transition-colors">
                <div className="bg-gradient-to-br from-gray-50 to-white p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-xl font-bold text-gray-900">Wood</h4>
                    <span className="text-xs font-semibold bg-stone-100 text-stone-700 px-3 py-1 rounded-full">Timeless</span>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed mb-5">
                    A solid wood door is a statement piece. Crafted from species like mahogany, oak, or fir, each door is unique. It can be carved, stained, or painted to match any architectural style — and it adds real, measurable value to your home.
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2 text-gray-700"><i className="ri-check-line text-brand"></i> Unique grain — no two doors alike</li>
                    <li className="flex items-center gap-2 text-gray-700"><i className="ri-check-line text-brand"></i> Fully customizable carvings &amp; profiles</li>
                    <li className="flex items-center gap-2 text-gray-700"><i className="ri-check-line text-brand"></i> Refinishable — looks new for decades</li>
                    <li className="flex items-center gap-2 text-gray-700"><i className="ri-check-line text-brand"></i> Highest perceived home value boost</li>
                  </ul>
                </div>
                <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-xs text-gray-500">Best for</span>
                  <span className="text-xs font-semibold text-gray-700">Luxury &amp; custom homes</span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom note */}
          <div className="mt-12 rounded-xl bg-gradient-to-r from-brand/8 to-brand/4 border border-brand/20 p-6 flex flex-col sm:flex-row items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center bg-brand/10 rounded-full flex-shrink-0">
              <i className="ri-information-line text-brand text-2xl"></i>
            </div>
            <p className="text-gray-700 text-sm leading-relaxed">
              <strong className="text-gray-900">Not sure which material is right for you?</strong> Our team will assess your home&apos;s architecture, local climate, and budget to recommend the best option — at no cost. Every installation comes with a full manufacturer warranty and our own workmanship guarantee.
            </p>
            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                window.REACT_APP_NAVIGATE('/');
                setTimeout(() => {
                  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                }, 100);
              }}
              className="flex-shrink-0 px-6 py-3 bg-brand text-white rounded-lg font-semibold text-sm hover:bg-brand-dark transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Expert Advice
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden" style={{backgroundImage: 'url(https://public.readdy.ai/ai/img_res/6824cd704e8256844dcfb15f8fd6b76d.jpg)', backgroundSize: 'cover', backgroundPosition: 'center'}}>
        <div className="absolute inset-0 bg-black/55"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Transform Your Home Today
          </h2>
          <p className="text-xl text-white/80 mb-8 leading-relaxed">
            Experience the difference quality windows and doors can make. Contact us for a free consultation and discover how we can enhance your home&apos;s comfort, beauty, and value.
          </p>
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              window.REACT_APP_NAVIGATE('/');
              setTimeout(() => {
                document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
              }, 100);
            }}
            className="inline-block px-10 py-4 bg-white text-brand rounded-lg font-bold text-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            Get Free Estimate
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
